import { Typography, Stack } from '@mui/material';
import { useCallback, useMemo, useState } from 'react';
import { Icon } from '@/units/index';
import { FilterGroup, NoteStatus } from '../enums';
import { TFilters } from '../types';
import { CountBadge } from '../components/ui/CountBadge';
import { TGroup } from '@/components/common/GroupSelect';
import { useNotesContext } from './useNotesContext';

const filtersDefaultValues: TFilters = {
  status: [],
  tags: [],
};

export const useNotesFilterInit = () => {
  const { tour, tags } = useNotesContext();
  const [filters, setFilters] = useState<TFilters>(filtersDefaultValues);
  const [selectedFilterGroup, setSelectedFilterGroup] = useState('');

  const clearFilter = useCallback(
    (key?: keyof TFilters) => {
      if (key) {
        return setFilters((prev) => ({
          ...prev,
          [key]: filtersDefaultValues[key],
        }));
      }

      return setFilters(filtersDefaultValues);
    },
    [setFilters],
  );

  const getFiltersCount = useCallback(
    (key?: keyof TFilters) => {
      if (key) {
        return filters[key]?.length ?? 0;
      }

      return Object.values(filters).filter((group) => group.length).length;
    },
    [filters],
  );

  const renderGroupFiltersCount = useCallback(
    (group: FilterGroup): React.ReactNode => {
      const appliedFiltersCount = getFiltersCount(group);

      return appliedFiltersCount > 0 ? <CountBadge count={appliedFiltersCount} /> : null;
    },
    [getFiltersCount],
  );

  const filterGroups: TGroup<FilterGroup>[] = useMemo(
    () => [
      {
        label: 'Status',
        icon: <Icon.Bolt sx={{ color: 'transparent' }} />,
        name: FilterGroup.Status,
        afterContent: renderGroupFiltersCount(FilterGroup.Status),
        options: [
          {
            label: 'Completed',
            value: NoteStatus.Completed,
            icon: <i className="fa fa-check" style={{ color: '#12B76A', fontSize: '18px' }} />,
          },
          {
            label: 'Incompleted',
            value: NoteStatus.Incompleted,
            icon: (
              <i
                className="fa-regular fa-arrow-rotate-left"
                style={{ color: '#FB6514', fontSize: '18px' }}
              />
            ),
          },
        ],
      },
      {
        label: 'Tags',
        icon: <Icon.CircleTag sx={{ fill: '#667085' }} />,
        name: FilterGroup.Tags,
        searchEnabled: true,
        hidden: !tour.tagNotesEnabled,
        afterContent: renderGroupFiltersCount(FilterGroup.Tags),
        options: tags.map((tag) => ({
          label: tag.title,
          value: tag.id,
          searchTerms: [tag.title],
          icon: <Icon.CircleTag sx={{ fontSize: '20px' }} />,
        })),
        renderValue: (value) => {
          const tag = tags.find((t) => t.id.toString() === value.toString());

          return (
            <Stack sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                sx={{
                  display: '-webkit-box',
                  WebkitBoxOrient: 'vertical',
                  WebkitLineClamp: 1,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  overflowWrap: 'break-word',
                  color: 'var(--Black, #000)',
                  fontFamily: 'Inter',
                  fontSize: '16px',
                  fontStyle: 'normal',
                  fontWeight: 400,
                  lineHeight: '24px',
                }}
              >
                {tag.title}
              </Typography>
              <Typography
                sx={{
                  display: '-webkit-box',
                  WebkitBoxOrient: 'vertical',
                  WebkitLineClamp: 1,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  overflowWrap: 'break-word',
                  color: 'var(--Gray-500, #667085)',
                  fontFamily: 'Inter',
                  fontSize: '14px',
                  fontStyle: 'normal',
                  fontWeight: 400,
                  lineHeight: '20px',
                }}
              >
                <span dangerouslySetInnerHTML={{ __html: tag.description }} />
              </Typography>
            </Stack>
          );
        },
      },
    ],
    [renderGroupFiltersCount, tags, tour.tagNotesEnabled],
  );

  return {
    filters,
    setFilters,
    clearFilter,
    selectedFilterGroup,
    setSelectedFilterGroup,
    filterGroups,
    getFiltersCount,
  };
};
